<script setup>
import { reactive, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import mcpConnectors from 'dashboard/api/captain/mcpConnectors';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import AuthConfig from '../customTool/AuthConfig.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  connector: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('captainMcpConnectors/getUIFlags'),
};

const initialState = {
  title: '',
  description: '',
  endpoint_url: '',
  transport: 'streamable_http',
  auth_type: 'none',
  auth_config: {},
  enabled: true,
};

const state = reactive({ ...initialState });

// Populate form when in edit mode
watch(
  () => props.connector,
  newConnector => {
    if (props.mode === 'edit' && newConnector && newConnector.id) {
      state.title = newConnector.title || '';
      state.description = newConnector.description || '';
      state.endpoint_url = newConnector.endpoint_url || '';
      state.transport = newConnector.transport || 'streamable_http';
      state.auth_type = newConnector.auth_type || 'none';
      state.auth_config = newConnector.auth_config || {};
      state.enabled =
        newConnector.enabled === undefined ? true : newConnector.enabled;
    }
  },
  { immediate: true }
);

const validationRules = {
  title: { required },
  endpoint_url: { required },
};

const transportOptions = computed(() => [
  {
    value: 'streamable_http',
    label: t('CAPTAIN.MCP_CONNECTORS.FORM.TRANSPORT_HTTP'),
  },
  {
    value: 'sse',
    label: t('CAPTAIN.MCP_CONNECTORS.FORM.TRANSPORT_SSE'),
    disabled: true,
  },
]);

const authTypeOptions = computed(() => [
  { value: 'none', label: t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.NONE') },
  { value: 'bearer', label: t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.BEARER') },
  { value: 'basic', label: t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.BASIC') },
  {
    value: 'api_key',
    label: t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.API_KEY'),
  },
]);

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() =>
  props.mode === 'edit'
    ? formState.uiFlags.value.updatingItem
    : formState.uiFlags.value.creatingItem
);

const formErrors = computed(() => ({
  title: v$.value.title.$error
    ? t('CAPTAIN.MCP_CONNECTORS.FORM.NAME_ERROR')
    : '',
  endpoint_url: v$.value.endpoint_url.$error
    ? t('CAPTAIN.MCP_CONNECTORS.FORM.ENDPOINT_ERROR')
    : '',
}));

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  emit('submit', state);
};

const isTesting = ref(false);
const testResult = ref(null);

const handleTest = async () => {
  if (!state.endpoint_url) return;

  isTesting.value = true;
  testResult.value = null;
  try {
    const { data } = await mcpConnectors.test(state);
    testResult.value = { success: true, count: data.tools?.length ?? 0 };
  } catch (e) {
    const message =
      e.response?.data?.error || t('CAPTAIN.MCP_CONNECTORS.TEST.ERROR');
    testResult.value = { success: false, message };
  } finally {
    isTesting.value = false;
  }
};
</script>

<template>
  <form
    class="flex flex-col px-4 -mx-4 gap-4 max-h-[calc(100vh-200px)] overflow-y-scroll"
    @submit.prevent="handleSubmit"
  >
    <Input
      v-model="state.title"
      :label="t('CAPTAIN.MCP_CONNECTORS.FORM.TITLE_LABEL')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <TextArea
      v-model="state.description"
      :label="t('CAPTAIN.CUSTOM_TOOLS.FORM.DESCRIPTION.LABEL')"
      :rows="2"
    />

    <Input
      v-model="state.endpoint_url"
      :label="t('CAPTAIN.MCP_CONNECTORS.FORM.ENDPOINT_LABEL')"
      :message="formErrors.endpoint_url"
      :message-type="formErrors.endpoint_url ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.MCP_CONNECTORS.FORM.TRANSPORT_LABEL') }}
      </label>
      <Select
        v-model="state.transport"
        :options="transportOptions"
        class="!w-full [&>select]:w-full"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.MCP_CONNECTORS.FORM.AUTH_TYPE_LABEL') }}
      </label>
      <Select
        v-model="state.auth_type"
        :options="authTypeOptions"
        class="!w-full [&>select]:w-full"
      />
    </div>

    <AuthConfig
      v-model:auth-config="state.auth_config"
      :auth-type="state.auth_type"
    />

    <div class="flex flex-col gap-2">
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-lucide-play"
        :label="t('CAPTAIN.MCP_CONNECTORS.TEST.BUTTON')"
        :is-loading="isTesting"
        :disabled="isTesting || !state.endpoint_url"
        @click="handleTest"
      />
      <div
        v-if="testResult"
        class="flex items-center gap-2 px-3 py-2 text-xs rounded-lg"
        :class="
          testResult.success
            ? 'bg-n-teal-2 text-n-teal-11'
            : 'bg-n-ruby-2 text-n-ruby-11'
        "
      >
        <span
          :class="
            testResult.success ? 'i-lucide-check-circle' : 'i-lucide-x-circle'
          "
          class="size-3.5 shrink-0"
        />
        {{
          testResult.success
            ? t('CAPTAIN.MCP_CONNECTORS.TEST.SUCCESS', {
                count: testResult.count,
              })
            : testResult.message
        }}
      </div>
    </div>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-violet-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="
          t(mode === 'edit' ? 'CAPTAIN.FORM.EDIT' : 'CAPTAIN.FORM.CREATE')
        "
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
